import React, { useMemo, useState } from 'react'
import {
  Button,
  Input,
  List,
  Modal,
  Popconfirm,
  Select,
  Tag,
  Typography,
} from 'antd'
import { TeamOutlined, FormOutlined, ShoppingOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'

const { Text } = Typography

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const WayfindingBanner = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d3adf7;
  background: linear-gradient(90deg, #f9f0ff 0%, #f0f9ff 100%);
  font-size: 12px;
  line-height: 1.5;
  color: #434343;
`

const Section = styled.section`
  min-width: 0;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'list'
        ? '#91caff'
        : $variant === 'details'
        ? '#b7eb8f'
        : '#d3adf7'};
  background: ${({ $variant }) =>
    $variant === 'list'
      ? 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%)'
      : $variant === 'details'
      ? '#fcfff9'
      : 'linear-gradient(180deg, #faf5ff 0%, #ffffff 100%)'};
`

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
`

const SectionIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ffffff;
  background: ${({ $variant }) =>
    $variant === 'list'
      ? '#1677ff'
      : $variant === 'details'
      ? '#52c41a'
      : '#722ed1'};
  font-size: 14px;
`

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const SectionHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  margin-top: 2px;
  line-height: 1.45;
`

const GroupsList = styled(List)`
  .ant-list-item {
    padding: 12px !important;
    border: 1px solid #e6f4ff !important;
    border-radius: 8px !important;
    margin-bottom: 8px !important;
    background: #ffffff;
    display: block !important;
  }

  .ant-list-item:last-child {
    margin-bottom: 0 !important;
  }
`

const GroupCardTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const GroupCardMeta = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
  margin-top: 4px;
`

const GroupCardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
`

const SubSection = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px dashed
    ${({ $variant }) => ($variant === 'products' ? '#d3adf7' : '#b7eb8f')};
  background: #ffffff;
`

const SubSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #595959;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(130px, 1fr) minmax(130px, 1fr);
  gap: 8px;
  margin-top: 6px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const BulkActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`

const EditorActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const emptyDraft = {
  id: null,
  name: '',
  notes: '',
  selectedProductKeys: [],
}

const ManageGroupsModal = ({
  visible = false,
  onCancel = () => {},
  groups = [],
  products = [],
  onCreateGroup = () => {},
  onUpdateGroup = () => {},
  onDeleteGroup = () => {},
  onDuplicateGroup = () => {},
}) => {
  const [draft, setDraft] = useState(emptyDraft)
  const [productSearch, setProductSearch] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('ALL')
  const [typeFilter, setTypeFilter] = useState('ALL')

  const isEditing = Boolean(draft.id)
  const productOptions = useMemo(() => {
    const unique = new Map()
    ;(products || []).forEach(rawProduct => {
      const selectionKey = String(rawProduct?.selectionKey || '').trim()
      if (!selectionKey || unique.has(selectionKey)) return
      unique.set(selectionKey, {
        selectionKey,
        label: rawProduct?.label || 'Unnamed product',
        title: rawProduct?.title || '',
        supplierTitle: rawProduct?.supplierTitle || '',
        type: rawProduct?.type || '',
        sku: rawProduct?.sku || '',
        stockTakeProductId: rawProduct?.stockTakeProductId || null,
        venueProductId: rawProduct?.venueProductId || null,
      })
    })
    return [...unique.values()]
  }, [products])

  const optionByKey = useMemo(
    () =>
      Object.fromEntries(
        productOptions.map(option => [option.selectionKey, option]),
      ),
    [productOptions],
  )

  const supplierOptions = useMemo(() => {
    const uniqueSuppliers = new Set(
      productOptions
        .map(option => String(option?.supplierTitle || '').trim())
        .filter(Boolean),
    )
    return ['ALL', ...[...uniqueSuppliers].sort((a, b) => a.localeCompare(b))]
  }, [productOptions])

  const typeOptions = useMemo(() => {
    const uniqueTypes = new Set(
      productOptions
        .map(option => String(option?.type || '').trim())
        .filter(Boolean),
    )
    return ['ALL', ...[...uniqueTypes].sort((a, b) => a.localeCompare(b))]
  }, [productOptions])

  const filteredProductOptions = useMemo(() => {
    const searchValue = String(productSearch || '')
      .trim()
      .toLowerCase()
    return productOptions.filter(option => {
      if (
        supplierFilter !== 'ALL' &&
        String(option?.supplierTitle || '').trim() !== supplierFilter
      ) {
        return false
      }
      if (
        typeFilter !== 'ALL' &&
        String(option?.type || '').trim() !== typeFilter
      ) {
        return false
      }
      if (!searchValue) return true
      return String(option?.label || '')
        .toLowerCase()
        .includes(searchValue)
    })
  }, [productOptions, productSearch, supplierFilter, typeFilter])

  const filteredKeys = useMemo(
    () => filteredProductOptions.map(option => option.selectionKey),
    [filteredProductOptions],
  )

  const mergeUniqueKeys = values => [...new Set((values || []).filter(Boolean))]

  const handleAddFilteredProducts = () => {
    setDraft(prev => ({
      ...prev,
      selectedProductKeys: mergeUniqueKeys([
        ...(prev.selectedProductKeys || []),
        ...filteredKeys,
      ]),
    }))
  }

  const handleRemoveFilteredProducts = () => {
    const filteredSet = new Set(filteredKeys)
    setDraft(prev => ({
      ...prev,
      selectedProductKeys: (prev.selectedProductKeys || []).filter(
        key => !filteredSet.has(key),
      ),
    }))
  }

  const handleSelectAllProducts = () => {
    setDraft(prev => ({
      ...prev,
      selectedProductKeys: mergeUniqueKeys(
        productOptions.map(option => option.selectionKey),
      ),
    }))
  }

  const handleClearAllProducts = () => {
    if ((draft.selectedProductKeys || []).length > 20) {
      Modal.confirm({
        title: 'Clear all selected products?',
        content:
          'This group has many selected products. Are you sure you want to clear the full selection?',
        okText: 'Clear all',
        cancelText: 'Cancel',
        onOk: () =>
          setDraft(prev => ({
            ...prev,
            selectedProductKeys: [],
          })),
      })
      return
    }
    setDraft(prev => ({
      ...prev,
      selectedProductKeys: [],
    }))
  }

  const isDraftValid =
    String(draft.name || '').trim().length > 0 &&
    Array.isArray(draft.selectedProductKeys) &&
    draft.selectedProductKeys.length > 0

  const sortedGroups = useMemo(
    () =>
      [...(groups || [])].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || '')),
      ),
    [groups],
  )

  const resetDraft = () => setDraft(emptyDraft)

  const handleSubmit = async () => {
    if (!isDraftValid) return
    const payload = {
      name: String(draft.name || '').trim(),
      notes: String(draft.notes || '').trim(),
      productIds: draft.selectedProductKeys
        .map(key => optionByKey[key]?.stockTakeProductId)
        .filter(Boolean),
      venueProductIds: draft.selectedProductKeys
        .map(key => optionByKey[key]?.venueProductId)
        .filter(Boolean),
      productKeys: draft.selectedProductKeys.filter(Boolean),
    }

    if (isEditing) {
      await onUpdateGroup(draft.id, payload)
    } else {
      await onCreateGroup(payload)
    }
    resetDraft()
  }

  const handleEdit = group => {
    const existingProductKeys = Array.isArray(group?.productKeys)
      ? group.productKeys
          .filter(Boolean)
          .map(value => String(value).trim().toLowerCase())
      : []
    const existingProductIds = Array.isArray(group?.productIds)
      ? group.productIds.map(value => String(value))
      : []

    const selectedProductKeys = existingProductKeys.length
      ? existingProductKeys
      : productOptions
          .filter(option =>
            existingProductIds.includes(
              String(option.stockTakeProductId || ''),
            ),
          )
          .map(option => option.selectionKey)

    setDraft({
      id: group?._id || group?.id || null,
      name: group?.name || '',
      notes: group?.notes || '',
      selectedProductKeys,
    })
  }

  return (
    <Modal
      title='Manage Partial Count Groups'
      visible={visible}
      onCancel={() => {
        resetDraft()
        onCancel()
      }}
      footer={null}
      width={980}
      destroyOnClose
    >
      <ModalBody>
        <WayfindingBanner>
          <strong>How partial groups work:</strong> create a group, add
          products, then start a partial count from that group. Blue = existing
          groups, purple = editor, green = group details, dashed purple =
          product selection.
        </WayfindingBanner>

        <Section $variant='list'>
          <SectionHeader>
            <SectionIcon $variant='list'>
              <TeamOutlined />
            </SectionIcon>
            <div>
              <SectionTitle>Existing groups</SectionTitle>
              <SectionHint>
                Edit, duplicate, or delete saved partial count groups.
              </SectionHint>
            </div>
            <Tag color='blue' style={{ marginLeft: 'auto' }}>
              {sortedGroups.length} group{sortedGroups.length === 1 ? '' : 's'}
            </Tag>
          </SectionHeader>
          <GroupsList
            style={{
              maxHeight: 280,
              overflowY: 'auto',
            }}
            dataSource={sortedGroups}
            locale={{ emptyText: 'No partial groups created yet.' }}
            renderItem={group => {
              const productCount =
                group?.productCount ||
                group?.venueProductIds?.length ||
                group?.productKeys?.length ||
                group?.productIds?.length ||
                0

              return (
                <List.Item>
                  <GroupCardTitle>
                    {group?.name || 'Untitled group'}
                  </GroupCardTitle>
                  <GroupCardMeta>
                    {group?.notes || 'No details provided'}
                  </GroupCardMeta>
                  <div style={{ marginTop: 6 }}>
                    <Tag color='geekblue'>
                      {productCount} product{productCount === 1 ? '' : 's'}
                    </Tag>
                  </div>
                  <GroupCardActions>
                    <Button icon='edit' onClick={() => handleEdit(group)}>
                      Edit
                    </Button>
                    <Button icon='copy' onClick={() => onDuplicateGroup(group)}>
                      Duplicate
                    </Button>
                    <Popconfirm
                      title='Delete this group?'
                      okText='Delete'
                      cancelText='Cancel'
                      onConfirm={() => onDeleteGroup(group?._id || group?.id)}
                    >
                      <Button type='danger' icon='delete'>
                        Delete
                      </Button>
                    </Popconfirm>
                  </GroupCardActions>
                </List.Item>
              )
            }}
          />
        </Section>

        <Section $variant='editor'>
          <SectionHeader>
            <SectionIcon $variant='editor'>
              <FormOutlined />
            </SectionIcon>
            <div>
              <SectionTitle>
                {isEditing ? 'Edit group' : 'Create group'}
              </SectionTitle>
              <SectionHint>
                Groups define which products are included when you start a
                partial count.
              </SectionHint>
            </div>
            {isEditing ? (
              <Tag color='purple' style={{ marginLeft: 'auto' }}>
                Editing
              </Tag>
            ) : null}
          </SectionHeader>

          <SubSection $variant='details'>
            <SubSectionTitle>Group details</SubSectionTitle>
            <Input
              value={draft.name}
              placeholder='Group name (e.g. Wet Stock, Freezer)'
              onChange={event =>
                setDraft(prev => ({ ...prev, name: event.target.value }))
              }
              style={{ marginBottom: 8 }}
            />
            <Input.TextArea
              rows={3}
              value={draft.notes}
              placeholder='Optional notes (e.g. walk-in freezer, bar only)'
              onChange={event =>
                setDraft(prev => ({ ...prev, notes: event.target.value }))
              }
            />
          </SubSection>

          <SubSection $variant='products'>
            <SubSectionTitle>
              <ShoppingOutlined style={{ marginRight: 6 }} />
              Products in this group
            </SubSectionTitle>
            <FilterGrid>
              <Input
                value={productSearch}
                placeholder='Search title, supplier, SKU'
                onChange={event => setProductSearch(event.target.value)}
              />
              <Select
                value={supplierFilter}
                onChange={value => setSupplierFilter(value)}
                style={{ width: '100%' }}
              >
                {supplierOptions.map(value => (
                  <Select.Option key={`supplier-${value}`} value={value}>
                    {value === 'ALL' ? 'All suppliers' : value}
                  </Select.Option>
                ))}
              </Select>
              <Select
                value={typeFilter}
                onChange={value => setTypeFilter(value)}
                style={{ width: '100%' }}
              >
                {typeOptions.map(value => (
                  <Select.Option key={`type-${value}`} value={value}>
                    {value === 'ALL' ? 'All types' : value}
                  </Select.Option>
                ))}
              </Select>
            </FilterGrid>
            <BulkActionRow>
              <Button
                onClick={handleAddFilteredProducts}
                disabled={filteredKeys.length === 0}
              >
                Add filtered ({filteredKeys.length})
              </Button>
              <Button
                onClick={handleRemoveFilteredProducts}
                disabled={
                  filteredKeys.length === 0 ||
                  draft.selectedProductKeys.length === 0
                }
              >
                Remove filtered
              </Button>
              <Button
                onClick={handleSelectAllProducts}
                disabled={productOptions.length === 0}
              >
                Select all ({productOptions.length})
              </Button>
              <Button
                onClick={handleClearAllProducts}
                disabled={draft.selectedProductKeys.length === 0}
              >
                Clear selected
              </Button>
            </BulkActionRow>
            <Select
              mode='multiple'
              showSearch
              value={draft.selectedProductKeys}
              style={{ width: '100%', marginTop: 8 }}
              placeholder='Search and select products for this partial group'
              optionFilterProp='children'
              listHeight={280}
              onSearch={value => setProductSearch(value)}
              filterOption={false}
              onChange={values =>
                setDraft(prev => ({
                  ...prev,
                  selectedProductKeys: values || [],
                }))
              }
            >
              {filteredProductOptions.map(option => (
                <Select.Option
                  key={option.selectionKey}
                  value={option.selectionKey}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            <Text type='secondary' style={{ display: 'block', marginTop: 6 }}>
              <Tag
                color={
                  draft.selectedProductKeys.length > 0 ? 'green' : 'default'
                }
                style={{ marginRight: 6 }}
              >
                {draft.selectedProductKeys.length} selected
              </Tag>
              Showing {filteredProductOptions.length} of {productOptions.length}{' '}
              products
            </Text>
          </SubSection>

          <EditorActions>
            <Button
              onClick={resetDraft}
              disabled={
                !isEditing &&
                !draft.name &&
                !draft.notes &&
                (!draft.selectedProductKeys ||
                  draft.selectedProductKeys.length === 0)
              }
            >
              Clear
            </Button>
            <Button
              type='primary'
              icon='plus'
              onClick={handleSubmit}
              disabled={!isDraftValid}
            >
              {isEditing ? 'Save Group' : 'Create Group'}
            </Button>
          </EditorActions>
        </Section>
      </ModalBody>
    </Modal>
  )
}

export default ManageGroupsModal
